import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import en from '../i18n/en.json';
import es from '../i18n/es.json';
import pt from '../i18n/pt.json';
import { Language } from './types';

type TranslationParams = Record<string, string | number>;
type Dictionary = Record<string, string>;

const dictionaries: Record<Language, Dictionary> = { pt, en, es };
const LANGUAGE_STORAGE_KEY = 'orthoprecision_language';

const normalizeLanguage = (value: string | null | undefined): Language => {
  if (value === 'en' || value === 'es') return value;
  return 'pt';
};

const detectInitialLanguage = (): Language => {
  if (typeof window === 'undefined') return 'pt';

  const saved = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (saved) return normalizeLanguage(saved);

  const browserLanguage = window.navigator.language.toLowerCase();
  if (browserLanguage.startsWith('es')) return 'es';
  if (browserLanguage.startsWith('en')) return 'en';
  return 'pt';
};

const htmlLangByLanguage: Record<Language, string> = {
  pt: 'pt-BR',
  en: 'en',
  es: 'es',
};

const interpolate = (message: string, params?: TranslationParams): string => {
  if (!params) return message;

  const doubleBraceInterpolated = message.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    const value = params[key];
    return value === undefined ? `{{${key}}}` : String(value);
  });

  return doubleBraceInterpolated.replace(/\{(\w+)\}/g, (_, key) => {
    const value = params[key];
    return value === undefined ? `{${key}}` : String(value);
  });
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: TranslationParams) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(detectInitialLanguage);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
      document.documentElement.lang = htmlLangByLanguage[language];
    }
  }, [language]);

  const t = useMemo(() => {
    const currentDictionary = dictionaries[language];
    const fallbackDictionary = dictionaries.pt;

    return (key: string, params?: TranslationParams) => {
      const message = currentDictionary[key] || fallbackDictionary[key] || key;
      return interpolate(message, params);
    };
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
